
import React from 'react';
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "@/hooks/use-toast";
import { StaffShift } from '@/models/attendanceModel';
import { useTranslation } from 'react-i18next';

interface ShiftScheduleFormProps {
  storeId: string;
  staffId: string;
  existingShift?: StaffShift;
  onSubmit: (data: Omit<StaffShift, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  onCancel?: () => void;
}

const FormSchema = z.object({
  day_of_week: z.string(),
  start_time: z.string(),
  end_time: z.string(),
}).refine(data => {
  // Convert to 24-hour format for comparison
  const [startHour, startMinute] = data.start_time.split(':').map(Number);
  const [endHour, endMinute] = data.end_time.split(':').map(Number);
  
  const startMinutes = startHour * 60 + startMinute;
  const endMinutes = endHour * 60 + endMinute;
  
  return endMinutes > startMinutes;
}, {
  message: "End time must be after start time",
  path: ["end_time"],
});

const dayOptions = [
  { value: '0', label: 'Sunday' },
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
];

// Generate time options in 30-minute intervals (00:00 to 23:30)
const generateTimeOptions = () => {
  const options = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const formattedHour = hour.toString().padStart(2, '0');
      const formattedMinute = minute.toString().padStart(2, '0');
      options.push({
        value: `${formattedHour}:${formattedMinute}`,
        label: `${hour % 12 || 12}:${formattedMinute} ${hour >= 12 ? 'PM' : 'AM'}`,
      });
    }
  }
  return options;
};

const timeOptions = generateTimeOptions();

export const ShiftScheduleForm = ({ 
  storeId, 
  staffId, 
  existingShift, 
  onSubmit, 
  onCancel 
}: ShiftScheduleFormProps) => {
  const { t } = useTranslation();
  
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      day_of_week: existingShift ? existingShift.day_of_week.toString() : '1',
      start_time: existingShift ? existingShift.start_time : '09:00',
      end_time: existingShift ? existingShift.end_time : '17:00',
    },
  });

  const handleSubmit = async (data: z.infer<typeof FormSchema>) => {
    try {
      await onSubmit({
        staff_id: staffId,
        store_id: storeId,
        day_of_week: parseInt(data.day_of_week),
        start_time: data.start_time,
        end_time: data.end_time,
      });
      
      const selectedDay = dayOptions.find(d => d.value === data.day_of_week)?.label || '';
      
      toast({
        title: t('shiftScheduleUpdated'),
        description: t('shiftUpdated', { day: selectedDay }),
      });
      
      if (onCancel) onCancel();
    } catch (error) {
      toast({
        title: t('errorSavingShift'),
        description: t('errorSavingShiftDescription'),
        variant: "destructive",
      });
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="day_of_week"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('dayOfWeek')}</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select day" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {dayOptions.map((day) => (
                    <SelectItem key={day.value} value={day.value}>
                      {day.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <div className="flex flex-col sm:flex-row gap-4">
          <FormField
            control={form.control}
            name="start_time"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormLabel>{t('startTime')}</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {timeOptions.map((time) => (
                      <SelectItem key={time.value} value={time.value}>
                        {time.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="end_time"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormLabel>{t('endTime')}</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {timeOptions.map((time) => (
                      <SelectItem key={time.value} value={time.value}>
                        {time.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        <FormDescription className="text-xs">
          {t('shiftScheduleDescription')}
        </FormDescription>

        <div className="flex justify-between">
          <Button type="button" variant="outline" onClick={onCancel}>
            {t('cancel')}
          </Button>
          <Button type="submit">
            {existingShift ? t('updateShift') : t('addNewShift')}
          </Button>
        </div>
      </form>
    </Form>
  );
};
